/**
 * Reading a share count off a form, strictly.
 *
 * Shared by every form that takes one, so they cannot drift apart in what they
 * accept. `type="number"` inputs still hand back arbitrary strings, and
 * `parseInt` accepts trailing rubbish — `parseInt('100abc')` is 100, which would
 * write a number nobody typed.
 */

export type ParsedShareCount =
  // Discriminated on a string, not a boolean: tsconfig.app.json sets
  // `strict: false`, and without strictNullChecks an `ok: true | false`
  // discriminant does not narrow.
  { kind: 'count'; value: number } | { kind: 'invalid'; error: string };

const WHOLE_NUMBER = /^[+-]?\d+$/;

export const parseShareCount = (raw: string | number): ParsedShareCount => {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return { kind: 'invalid', error: 'Enter a number of shares' };
    if (!Number.isInteger(raw)) return { kind: 'invalid', error: 'Shares must be a whole number' };
    if (!Number.isSafeInteger(raw))
      return { kind: 'invalid', error: 'That number of shares is too large' };
    return { kind: 'count', value: raw };
  }

  const trimmed = raw.trim();
  if (trimmed === '') return { kind: 'invalid', error: 'Enter a number of shares' };
  if (!WHOLE_NUMBER.test(trimmed))
    return { kind: 'invalid', error: 'Enter a whole number of shares' };

  const value = Number(trimmed);
  if (!Number.isSafeInteger(value))
    return { kind: 'invalid', error: 'That number of shares is too large' };

  return { kind: 'count', value };
};
